if (!customElements.get("kit-variant-content")) {
  customElements.define(
    "kit-variant-content",
    class KitVariantContent extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector("[data-kit-variant-content]");
        this.updateSectionVisibility();
        this.addEventListener("click", this.handleBuyClick);

        if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
          return;
        }

        this.unsubscribeVariantChange = subscribe(
          PUB_SUB_EVENTS.productVariantChange,
          ({ variant }) => {
            if (variant?.id) this.renderVariant(variant.id);
          },
        );
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleBuyClick);
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      }

      handleBuyClick = (event) => {
        const buyTrigger = event.target.closest("[data-kit-buy-trigger]");
        if (!buyTrigger || !this.contains(buyTrigger) || buyTrigger.disabled) return;

        const heroAddButton = document.querySelector(
          `kit-preset-purchase[data-product-id="${this.dataset.productId}"] .kit-v2-hero-form [name='add']`,
        );
        if (!heroAddButton || heroAddButton.disabled) return;

        event.preventDefault();
        heroAddButton.click();
      };

      renderVariant(variantId) {
        const previousSavings = this.content?.querySelector("[data-kit-savings-value]");
        const previousSavingsCents = previousSavings
          ? Number(previousSavings.dataset.kitSavingsValue)
          : null;
        const template = Array.from(
          this.querySelectorAll("template[data-kit-variant-id]"),
        ).find((item) => item.dataset.kitVariantId === String(variantId));

        if (!template || !this.content) return;

        this.content.replaceChildren(template.content.cloneNode(true));
        this.updateSectionVisibility();

        this.dispatchEvent(
          new CustomEvent("kit:variant-content-rendered", {
            bubbles: true,
            detail: {
              productId: this.dataset.productId,
              variantId: String(variantId),
            },
          }),
        );

        const nextSavings = this.content.querySelector("[data-kit-savings-value]");
        const nextSavingsCents = nextSavings
          ? Number(nextSavings.dataset.kitSavingsValue)
          : null;
        const savingsChanged =
          Number.isFinite(previousSavingsCents) &&
          Number.isFinite(nextSavingsCents) &&
          previousSavingsCents !== nextSavingsCents;
        const usesPresetVolumePricing = Boolean(
          nextSavings?.closest("[data-kit-preset-volume-pricing]"),
        );

        if (!savingsChanged || usesPresetVolumePricing) return;

        animateKitSavings(nextSavings, previousSavingsCents, nextSavingsCents);

        const badge = this.content.querySelector("[data-kit-savings-percent]");
        if (badge && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          badge.classList.add("is-updating");
          window.setTimeout(() => badge.classList.remove("is-updating"), 500);
        }
      }

      updateSectionVisibility() {
        const conditionalSection = this.closest(".kit-v2-shell--conditional");
        if (!conditionalSection || !this.content) return;

        conditionalSection.hidden = !this.content.querySelector("[data-kit-section-body]");
      }
    },
  );
}

if (!customElements.get("kit-hero-gallery")) {
  customElements.define(
    "kit-hero-gallery",
    class KitHeroGallery extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this.handleClick);
        this.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        this.removeEventListener("keydown", this.handleKeydown);
      }

      handleClick = (event) => {
        const thumbnail = event.target.closest("[data-kit-gallery-thumb]");
        if (!thumbnail || !this.contains(thumbnail)) return;

        this.activateImage(thumbnail.dataset.kitGalleryThumb);
      };

      handleKeydown = (event) => {
        const thumbnail = event.target.closest("[data-kit-gallery-thumb]");
        if (
          !thumbnail ||
          !this.contains(thumbnail) ||
          !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
        ) {
          return;
        }

        const thumbnails = Array.from(this.querySelectorAll("[data-kit-gallery-thumb]"));
        const currentIndex = thumbnails.indexOf(thumbnail);
        if (currentIndex < 0) return;

        event.preventDefault();
        let nextIndex = currentIndex;

        if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % thumbnails.length;
        if (event.key === "ArrowLeft") {
          nextIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length;
        }
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = thumbnails.length - 1;

        const nextThumbnail = thumbnails[nextIndex];
        this.activateImage(nextThumbnail.dataset.kitGalleryThumb);
        nextThumbnail.focus();
      };

      activateImage(selectedIndex) {
        this.querySelectorAll("[data-kit-gallery-slide]").forEach((slide) => {
          const active = slide.dataset.kitGallerySlide === String(selectedIndex);
          slide.classList.toggle("is-active", active);
          slide.hidden = !active;
        });

        this.querySelectorAll("[data-kit-gallery-thumb]").forEach((thumbnail) => {
          const active = thumbnail.dataset.kitGalleryThumb === String(selectedIndex);
          thumbnail.classList.toggle("is-active", active);
          thumbnail.setAttribute("aria-selected", String(active));
          thumbnail.tabIndex = active ? 0 : -1;
        });
      }
    },
  );
}

if (!customElements.get("kit-installed-gallery")) {
  customElements.define(
    "kit-installed-gallery",
    class KitInstalledGallery extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
      }

      handleClick = (event) => {
        const opener = event.target.closest("[data-kit-installed-lightbox-open]");
        const lightbox = this.querySelector("[data-kit-installed-lightbox]");

        if (opener && this.contains(opener) && lightbox) {
          const image = lightbox.querySelector("[data-kit-installed-lightbox-image]");
          if (!image || !opener.dataset.kitInstalledLightboxSrc) return;

          image.src = opener.dataset.kitInstalledLightboxSrc;
          image.alt = opener.dataset.kitInstalledLightboxAlt || "";
          lightbox.showModal();
          return;
        }

        const closeButton = event.target.closest("[data-kit-installed-lightbox-close]");
        if ((closeButton && this.contains(closeButton)) || event.target === lightbox) {
          lightbox?.close();
        }
      };
    },
  );
}

if (!customElements.get("kit-pdp-v2")) {
  customElements.define(
    "kit-pdp-v2",
    class KitPdpV2 extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this.handleClick);
        this.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        this.removeEventListener("keydown", this.handleKeydown);
      }

      handleClick = (event) => {
        const tab = event.target.closest("[data-kit-tab]");
        if (tab) {
          this.activateTab(tab);
          return;
        }

        const buyTrigger = event.target.closest("[data-kit-buy-trigger]");
        if (!buyTrigger || buyTrigger.disabled) return;

        const nativeAddButton = document.querySelector(
          ".kit-product-page product-form [name='add']",
        );

        if (nativeAddButton && !nativeAddButton.disabled) {
          nativeAddButton.click();
        }
      };

      handleKeydown = (event) => {
        const currentTab = event.target.closest("[data-kit-tab]");
        if (!currentTab || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
          return;
        }

        const tabSet = currentTab.closest("[data-kit-tabs]");
        const tabs = Array.from(tabSet?.querySelectorAll("[data-kit-tab]") || []);
        const currentIndex = tabs.indexOf(currentTab);
        if (currentIndex < 0) return;

        event.preventDefault();
        let nextIndex = currentIndex;

        if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;

        this.activateTab(tabs[nextIndex]);
        tabs[nextIndex].focus();
      };

      activateTab(tab) {
        const tabSet = tab.closest("[data-kit-tabs]");
        const selectedIndex = tab.dataset.kitTab;
        if (!tabSet) return;

        tabSet.querySelectorAll("[data-kit-tab]").forEach((item) => {
          const active = item === tab;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-selected", String(active));
          item.tabIndex = active ? 0 : -1;
        });

        tabSet.querySelectorAll("[data-kit-panel]").forEach((panel) => {
          const active = panel.dataset.kitPanel === selectedIndex;
          panel.classList.toggle("is-active", active);
          panel.hidden = !active;
        });
      }
    },
  );
}

function formatKitV2Money(cents, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(cents / 100);
  } catch (error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function animateKitSavings(element, fromCents, toCents) {
  if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const currency =
    element.closest("[data-kit-deal]")?.dataset.kitSavingsCurrency ||
    document.querySelector("[data-kit-deal]")?.dataset.kitSavingsCurrency;
  const start = performance.now();
  const duration = 500;

  const tick = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const value = fromCents + (toCents - fromCents) * eased;
    element.textContent = formatKitV2Money(value, currency);

    if (progress < 1) {
      window.requestAnimationFrame(tick);
    } else {
      element.textContent = formatKitV2Money(toCents, currency);
    }
  };

  window.requestAnimationFrame(tick);
}
